import React, { useEffect, useState } from 'react';
import { Sparkles, Plane, AlertTriangle, Bath, Triangle, Skull, Zap, Clock, DollarSign, CircleOff } from 'lucide-react';
import confetti from 'canvas-confetti';

// Icon mapping for specific aircraft
const AIRCRAFT_ICONS = {
  'spruce-moose': Bath,
  'the-bermudan': Triangle,
  'patient-zero': Skull,
  'passenger-zero': Skull,
  'market-maker': DollarSign,
  'schrodinger-jet': Zap,
  'the-perpetual': Clock,
  'agee-whells': CircleOff, // Radar-off style icon for the vanishing plane
};

/**
 * AnomalyModal - Celebratory centered notification for anomalous aircraft events
 * Shows for 5 seconds then auto-dismisses (museum events require button click)
 */
export default function AnomalyModal({ anomaly, onClose }) {
  const show = anomaly !== null && anomaly !== undefined;
  const [isVisible, setIsVisible] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);

  useEffect(() => {
    if (show) {
      // Animate in
      setIsVisible(true);
      setIsFadingOut(false);

      // Fire confetti for museum events
      if (anomaly.type === 'museum' || anomaly.type === 'permanent_ground') {
        const duration = 2000;
        const end = Date.now() + duration;

        const frame = () => {
          confetti({
            particleCount: 4,
            angle: 60,
            spread: 65,
            origin: { x: 0 },
            colors: ['#f59e0b', '#8b5cf6', '#ec4899', '#22c55e'],
          });
          confetti({
            particleCount: 4,
            angle: 120,
            spread: 65,
            origin: { x: 1 },
            colors: ['#f59e0b', '#8b5cf6', '#ec4899', '#22c55e'],
          });

          if (Date.now() < end) {
            requestAnimationFrame(frame);
          }
        };

        frame();
      }

      // Auto-dismiss for non-museum events after 5 seconds
      if (anomaly.type !== 'museum' && anomaly.type !== 'permanent_ground') {
        const fadeTimer = setTimeout(() => {
          setIsFadingOut(true);
        }, 4500);
        const closeTimer = setTimeout(() => {
          onClose();
        }, 5000);
        return () => {
          clearTimeout(fadeTimer);
          clearTimeout(closeTimer);
        };
      }
    } else {
      setIsVisible(false);
    }
  }, [show, anomaly?.type, onClose]);

  if (!show) return null;

  const handleClose = () => {
    onClose();
  };

  // Determine icon based on aircraft or type
  const getIcon = () => {
    // Check for specific aircraft icon
    const aircraftId = anomaly.aircraftId?.toLowerCase();
    const IconComponent = aircraftId ? AIRCRAFT_ICONS[aircraftId] : null;
    if (IconComponent) {
      return <IconComponent className="w-10 h-10" />;
    }

    if (anomaly.type === 'museum' || anomaly.type === 'permanent_ground') {
      return <Sparkles className="w-10 h-10" />;
    }
    if (anomaly.type === 'warning') {
      return <AlertTriangle className="w-10 h-10" />;
    }
    return <Plane className="w-10 h-10" />;
  };

  const isMuseum = anomaly.type === 'museum' || anomaly.type === 'permanent_ground';

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none"
      style={{
        opacity: isFadingOut ? 0 : 1,
        transition: 'opacity 0.5s ease-out',
      }}
    >
      {/* Backdrop for museum events only */}
      {isMuseum && (
        <div
          className="absolute inset-0 pointer-events-auto"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
          onClick={handleClose}
        />
      )}

      {/* Notification card - click anywhere to dismiss */}
      <div
        className="relative max-w-md mx-4 rounded-xl border-2 pointer-events-auto cursor-pointer"
        onClick={handleClose}
        style={{
          backgroundColor: '#0c0c0c',
          borderColor: isMuseum ? '#f59e0b' : '#8b5cf6',
          boxShadow: `0 0 60px ${isMuseum ? 'rgba(245, 158, 11, 0.4)' : 'rgba(139, 92, 246, 0.4)'}`,
          transform: isVisible ? 'scale(1) translateY(0)' : 'scale(0.9) translateY(20px)',
          opacity: isVisible && !isFadingOut ? 1 : 0,
          transition: 'all 0.3s ease-out',
          animation: isVisible && !isFadingOut ? 'anomalyPulse 2s ease-in-out infinite' : 'none',
        }}
      >
        {/* Top accent bar with glow */}
        <div
          className="absolute top-0 left-4 right-4 h-1 rounded-full"
          style={{
            background: isMuseum
              ? 'linear-gradient(90deg, #f59e0b, #ec4899, #8b5cf6)'
              : 'linear-gradient(90deg, #8b5cf6, #ec4899, #f59e0b)',
            boxShadow: `0 0 20px ${isMuseum ? '#f59e0b' : '#8b5cf6'}`,
          }}
        />

        <div className="p-6 pt-8 text-center">
          {/* Icon circle */}
          <div
            className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4"
            style={{
              background: isMuseum
                ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.3), rgba(139, 92, 246, 0.3))'
                : 'linear-gradient(135deg, rgba(139, 92, 246, 0.3), rgba(236, 72, 153, 0.3))',
              border: `2px solid ${isMuseum ? 'rgba(245, 158, 11, 0.6)' : 'rgba(139, 92, 246, 0.6)'}`,
              color: isMuseum ? '#f59e0b' : '#a78bfa',
            }}
          >
            {getIcon()}
          </div>

          {/* Title */}
          <h2
            className="text-2xl font-bold tracking-wide mb-1"
            style={{
              color: isMuseum ? '#f59e0b' : '#a78bfa',
              textShadow: `0 0 20px ${isMuseum ? 'rgba(245, 158, 11, 0.5)' : 'rgba(139, 92, 246, 0.5)'}`,
            }}
          >
            {anomaly.title || 'ANOMALOUS EVENT'}
          </h2>

          {/* Aircraft name subtitle (for anomaly_dispatch type) */}
          {anomaly.subtitle && anomaly.type === 'anomaly_dispatch' && (
            <h3
              className="text-xl font-bold tracking-widest mb-4"
              style={{
                color: '#f59e0b',
                textShadow: '0 0 15px rgba(245, 158, 11, 0.5)',
              }}
            >
              {anomaly.subtitle}
            </h3>
          )}

          {/* Tagline (quoted line for anomaly_dispatch) */}
          {anomaly.tagline && (
            <p
              className="text-lg italic mb-4"
              style={{ color: '#a78bfa' }}
            >
              "{anomaly.tagline}"
            </p>
          )}

          {/* Message */}
          <p
            className="text-lg leading-relaxed whitespace-pre-line"
            style={{ color: '#e5e5e5' }}
          >
            {anomaly.message}
          </p>

          {/* Subtitle for museum events (bottom position) */}
          {anomaly.subtitle && anomaly.type !== 'anomaly_dispatch' && (
            <div
              className="mt-4 py-2 px-4 rounded-lg inline-block"
              style={{
                backgroundColor: 'rgba(139, 92, 246, 0.2)',
                border: '1px solid rgba(139, 92, 246, 0.4)',
              }}
            >
              <span className="text-sm" style={{ color: '#a78bfa' }}>
                {anomaly.subtitle}
              </span>
            </div>
          )}

          {/* Button only for museum events */}
          {isMuseum && (
            <button
              onClick={handleClose}
              className="mt-6 w-full py-3 rounded-lg text-lg font-bold transition-all"
              style={{
                backgroundColor: '#f59e0b',
                color: '#000',
              }}
            >
              CONTINUE
            </button>
          )}
        </div>
      </div>

      {/* CSS keyframes for pulse animation */}
      <style>{`
        @keyframes anomalyPulse {
          0%, 100% {
            box-shadow: 0 0 40px ${isMuseum ? 'rgba(245, 158, 11, 0.3)' : 'rgba(139, 92, 246, 0.3)'};
          }
          50% {
            box-shadow: 0 0 60px ${isMuseum ? 'rgba(245, 158, 11, 0.5)' : 'rgba(139, 92, 246, 0.5)'};
          }
        }
      `}</style>
    </div>
  );
}
